import { Container, Graphics, Text, type TextStyleOptions } from 'pixi.js';
import { GlowFilter } from '@pixi/filter-glow';
import type { Player } from './player';
import { wave } from './waves';
import { settings } from './settings';
import { MOBILE_ZOOM } from './state';

const SHADOW = {
  color: 0x000000, alpha: 0.85, distance: 2, angle: Math.PI / 4, blur: 3,
};
function neonStyle(size: number, fill: number, weight: TextStyleOptions['fontWeight'] = 'normal'): TextStyleOptions {
  return {
    fontFamily: 'Courier New', fontSize: size, fill, fontWeight: weight,
    dropShadow: SHADOW,
  };
}

export class HUD {
  view: Container;
  private hpBar: Graphics;
  private shieldBar: Graphics;
  private bg: Graphics;
  private waveText: Text;
  private scoreText: Text;
  private weaponText: Text;
  private livesText: Text;
  private bombText: Text;
  private comboText: Text;
  private comboRing: Graphics;
  private synergyText: Text;
  private bannerText: Text;
  private bannerBg: Graphics;
  private summaryText: Text;
  private summaryBg: Graphics;
  private bossBar: Graphics;
  private bossBarGlow: GlowFilter | null = null;
  private bossPrevPhase = -1;
  private bossPulseT = 0;
  private bossBarBg: Graphics;
  private bossBarText: Text;
  private specialsRow: Graphics;
  private specialsLbl: Text;
  private addonRow: Text;
  specialCharges: number[] = [2, 1, 1, 1];
  endless = false;
  specialIdx = 0;
  bashCD = 0;
  missileTier = 0;
  ringActive = false;
  score = 0;
  weaponName = 'PULSE';
  weaponIdx = 0;
  lives = 3;
  bombCharges = 2;
  combo = 0;
  comboPulse = 0;
  synergyTimer = 0;

  constructor(stage: Container) {
    this.view = new Container();
    stage.addChild(this.view);

    this.bg = new Graphics();
    this.view.addChild(this.bg);
    this.shieldBar = new Graphics();
    this.view.addChild(this.shieldBar);
    this.hpBar = new Graphics();
    this.view.addChild(this.hpBar);

    this.waveText = new Text({ text: 'VAGUE 0', style: neonStyle(18, 0xffffff, 'bold') });
    this.waveText.x = 14; this.waveText.y = 56;
    this.view.addChild(this.waveText);

    this.scoreText = new Text({ text: 'SCORE 0', style: neonStyle(22, 0xffdd66, 'bold') });
    this.scoreText.x = 14; this.scoreText.y = 76;
    try {
      this.scoreText.filters = [new GlowFilter({ distance: 8, outerStrength: 1.2, innerStrength: 0.2, color: 0xffdd66, quality: 0.4 }) as any];
    } catch {}
    this.view.addChild(this.scoreText);

    this.weaponText = new Text({ text: 'PULSE', style: neonStyle(18, 0x00ffff, 'bold') });
    this.weaponText.x = 14; this.weaponText.y = 96;
    try {
      this.weaponText.filters = [new GlowFilter({ distance: 10, outerStrength: 1.4, innerStrength: 0.3, color: 0x00ffff, quality: 0.4 }) as any];
    } catch {}
    this.view.addChild(this.weaponText);

    this.livesText = new Text({ text: 'VIES ♦♦♦', style: neonStyle(16, 0xff8888, 'bold') });
    this.livesText.x = 14; this.livesText.y = 124;
    this.view.addChild(this.livesText);

    // bombText kept for back-compat but hidden — specialsRow already shows charges visually
    this.bombText = new Text({ text: '', style: neonStyle(14, 0xffaa00) });
    this.bombText.visible = false;
    this.view.addChild(this.bombText);

    this.comboRing = new Graphics();
    this.view.addChild(this.comboRing);
    this.comboText = new Text({ text: '', style: neonStyle(36, 0xff8800, 'bold') });
    this.comboText.anchor.set(1, 0);
    this.view.addChild(this.comboText);

    this.synergyText = new Text({ text: '', style: neonStyle(14, 0xff44ff, 'bold') });
    this.synergyText.x = 14; this.synergyText.y = 156;
    this.view.addChild(this.synergyText);

    this.bannerBg = new Graphics();
    this.view.addChild(this.bannerBg);
    this.bannerText = new Text({
      text: '',
      style: {
        fontFamily: 'Courier New',
        fontSize: 42,
        fill: 0xffffff,
        fontWeight: 'bold',
        align: 'center',
        letterSpacing: 4,
        dropShadow: {
          color: 0x000000, alpha: 0.85, distance: 3, angle: Math.PI / 4, blur: 5,
        },
      },
    });
    try {
      this.bannerText.filters = [new GlowFilter({ distance: 16, outerStrength: 1.6, innerStrength: 0.3, color: 0xffffff, quality: 0.4 }) as any];
    } catch {}
    this.bannerText.anchor.set(0.5, 0.5);
    this.view.addChild(this.bannerText);

    this.summaryBg = new Graphics();
    this.view.addChild(this.summaryBg);
    this.summaryText = new Text({
      text: '',
      style: { ...neonStyle(18, 0xffffff), align: 'center' },
    });
    this.summaryText.anchor.set(0.5, 0.5);
    this.view.addChild(this.summaryText);

    this.specialsRow = new Graphics();
    this.view.addChild(this.specialsRow);
    this.specialsLbl = new Text({
      text: '',
      style: { ...neonStyle(11, 0xffffff), align: 'left' },
    });
    this.view.addChild(this.specialsLbl);

    this.addonRow = new Text({ text: '', style: neonStyle(12, 0xff8800) });
    this.view.addChild(this.addonRow);

    this.bossBarBg = new Graphics();
    this.view.addChild(this.bossBarBg);
    this.bossBar = new Graphics();
    try {
      this.bossBarGlow = new GlowFilter({ distance: 12, outerStrength: 2.0, innerStrength: 0.4, color: 0xff00ff, quality: 0.4 });
      this.bossBar.filters = [this.bossBarGlow as any];
    } catch {}
    this.view.addChild(this.bossBar);
    this.bossBarText = new Text({ text: '', style: neonStyle(14, 0xffffff, 'bold') });
    this.bossBarText.anchor.set(0.5, 0);
    this.view.addChild(this.bossBarText);
  }

  // Smoothed bar values for animated transitions
  private hpDisp = 1;
  private shieldDisp = 1;
  private scoreDisp = 0;
  private bossHpDisp = 1;
  private _uiScale = 1;

  bossInfo: { hp: number; max: number; phase: number; kind: 'boss' | 'miniboss' } | null = null;

  render(player: Player, W: number, _H: number): void {
    const s = MOBILE_ZOOM;
    if (s !== this._uiScale) { this._uiScale = s; this.view.scale.set(s); }
    const VW = W / s;
    const VH = _H / s;
    const p = 14;
    const w = 220;
    const h = 14;
    const r = 4;

    // Lerp displayed values toward target (smooth bar transitions)
    const targetShield = player.shield / player.maxShield;
    const targetHp = player.hp / player.maxHp;
    this.shieldDisp += (targetShield - this.shieldDisp) * 0.18;
    this.hpDisp += (targetHp - this.hpDisp) * 0.18;

    this.bg.clear();
    this.bg.roundRect(p, p, w, h, r);
    this.bg.fill({ color: 0x001133, alpha: 0.85 });
    this.bg.stroke({ width: 1, color: 0x4488ff, alpha: 0.7 });
    this.bg.roundRect(p, p + 22, w, h, r);
    this.bg.fill({ color: 0x001a00, alpha: 0.85 });
    this.bg.stroke({ width: 1, color: 0x00ff55, alpha: 0.7 });

    this.shieldBar.clear();
    if (this.shieldDisp > 0.001) {
      this.shieldBar.roundRect(p, p, Math.max(2, w * this.shieldDisp), h, r);
      this.shieldBar.fill({ color: 0x4488ff });
    }

    this.hpBar.clear();
    const hpCol = player.hp > 15 ? 0x00ff55 : player.hp > 7 ? 0xffaa00 : 0xff2200;
    if (this.hpDisp > 0.001) {
      this.hpBar.roundRect(p, p + 22, Math.max(2, w * this.hpDisp), h, r);
      this.hpBar.fill({ color: hpCol });
    }
    // Low-HP pulse: draw an accent border that pulses red when HP under 30%
    const hpFrac = player.hp / player.maxHp;
    if (hpFrac > 0 && hpFrac < 0.3) {
      const pulse = 0.55 + 0.45 * Math.sin(Date.now() * 0.018);
      this.hpBar.roundRect(p - 2, p + 22 - 2, w + 4, h + 4, r + 1);
      this.hpBar.stroke({ width: 1.5, color: 0xff2200, alpha: 0.85 * pulse });
    }

    this.waveText.text = wave.wave > 0
      ? (this.endless ? `VAGUE ${wave.wave} ∞` : `VAGUE ${wave.wave} / 25`)
      : 'VAGUE 0';
    // Animated score counter — lerp displayed value toward target so the readout ticks up
    const diff = this.score - this.scoreDisp;
    if (Math.abs(diff) < 1) {
      this.scoreDisp = this.score;
    } else {
      this.scoreDisp += Math.sign(diff) * Math.max(1, Math.abs(diff) * 0.18);
    }
    this.scoreText.text = `SCORE ${Math.floor(this.scoreDisp).toLocaleString('fr-FR')}`;
    this.weaponText.text = `NIV ${this.weaponIdx + 1} : ${this.weaponName}`;
    // Tier-color the weapon label to match player glow (11 cosmic shades)
    {
      const tier = Math.min(10, Math.floor(this.weaponIdx / 9));
      const tierCols = [
        0x00ffff, 0x44ddff, 0xff44ff, 0xffaa00, 0xff2200, 0xffffaa,
        0xcc88ff, 0xff66ff, 0x88ffaa, 0xffcc44, 0xffffff,
      ];
      this.weaponText.style.fill = tierCols[tier];
      // Sync glow filter color too
      const filters = this.weaponText.filters as any;
      if (filters && filters[0]) filters[0].color = tierCols[tier];
    }
    this.livesText.text = 'VIES ' + '♦'.repeat(Math.max(0, this.lives + 1)) + '◇'.repeat(Math.max(0, 3 - this.lives - 1));
    this.livesText.style.fill = this.lives <= 0 ? 0xff2200 : this.lives === 1 ? 0xffaa00 : 0xff8888;
    this.bombText.text = `[1] BOMBE ×${this.bombCharges}`;
    this.bombText.style.fill = this.bombCharges > 0 ? 0xffaa00 : 0x444444;

    this.comboRing.clear();
    if (this.combo > 5) {
      const pulse = 1 + Math.max(0, this.comboPulse) * 0.04;
      this.comboText.text = `×${this.combo}`;
      this.comboText.x = VW - 16;
      this.comboText.y = 14;
      this.comboText.scale.set(pulse, pulse);
      const col = this.combo > 25 ? 0xffffff : this.combo > 12 ? 0xff2200 : 0xff8800;
      this.comboText.style.fill = col;
      if (this.comboPulse > 0) this.comboPulse -= 1;
      // Radial-wedge progress ring behind combo number
      const tiers = [5, 12, 25, 50];
      const next = tiers.find((t) => t > this.combo) ?? 100;
      const prev = tiers.filter((t) => t <= this.combo).slice(-1)[0] ?? 0;
      const progress = Math.max(0, Math.min(1, (this.combo - prev) / (next - prev)));
      const cx = VW - 16 - this.comboText.width / 2;
      const cy = 14 + this.comboText.height / 2;
      const r = Math.max(this.comboText.width, this.comboText.height) * 0.7;
      // Track
      this.comboRing.circle(cx, cy, r);
      this.comboRing.stroke({ width: 3, color: col, alpha: 0.18 });
      // Filled arc
      this.comboRing.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
      this.comboRing.stroke({ width: 3, color: col, alpha: 0.95 });
    } else {
      this.comboText.text = '';
    }

    if (this.synergyTimer > 0) {
      this.synergyText.text = `⚡ SYNERGIE ${Math.ceil(this.synergyTimer)}s`;
    } else {
      this.synergyText.text = '';
    }

    // Specials row bottom-left
    this.specialsRow.clear();
    const sx = 14;
    const sy = VH - 60;
    const slotW = 56, slotH = 32, slotG = 8;
    const labels = ['BOMBE', 'SURTENSION', 'RALENTI'];
    const cols = [0xffaa00, 0x4488ff, 0xffff44];
    for (let i = 0; i < 3; i++) {
      const x = sx + i * (slotW + slotG);
      const isActive = i === this.specialIdx;
      this.specialsRow.roundRect(x, sy, slotW, slotH, 6);
      this.specialsRow.fill({ color: isActive ? cols[i] : 0x111122, alpha: isActive ? 0.32 : 0.7 });
      this.specialsRow.stroke({ width: 2, color: cols[i], alpha: (settings.godMode || this.specialCharges[i] > 0) ? 1 : 0.3 });
    }
    let lblTxt = '';
    const inf = settings.godMode ? '∞' : '';
    for (let i = 0; i < 3; i++) {
      lblTxt += `[${i + 1}] ${labels[i]}×${settings.godMode ? inf : this.specialCharges[i]}  `;
    }
    if (settings.godMode) lblTxt = '⚡ DIEU  ' + lblTxt;
    this.specialsLbl.text = lblTxt;
    this.specialsLbl.x = sx;
    this.specialsLbl.y = sy + slotH + 2;

    // Addons row
    let addonTxt = '';
    if (this.bashCD > 0) addonTxt += `[E] CHOC ${this.bashCD.toFixed(1)}s   `;
    else addonTxt += `[E] CHOC ✓   `;
    if (this.missileTier > 0) addonTxt += `🚀×${this.missileTier}  `;
    if (this.ringActive) addonTxt += `⭕ANNEAU  `;
    this.addonRow.text = addonTxt;
    this.addonRow.x = 14;
    this.addonRow.y = VH - 18;

    // Boss HP bar at top center
    this.bossBar.clear();
    this.bossBarBg.clear();
    this.bossBarText.text = '';
    if (this.bossInfo) {
      const bw = Math.min(820, VW - 60);
      const bx = (VW - bw) / 2;
      const by = 18;
      const bh = 18;
      const br = 5;
      const targetBoss = Math.max(0, this.bossInfo.hp / this.bossInfo.max);
      this.bossHpDisp += (targetBoss - this.bossHpDisp) * 0.18;
      const phaseCol = this.bossInfo.phase === 0 ? 0xff00ff : this.bossInfo.phase === 1 ? 0xff8800 : 0xff0000;

      // Phase shift detection — punch the glow brighter for 0.5s and trigger a pulse
      if (this.bossInfo.phase !== this.bossPrevPhase) {
        this.bossPrevPhase = this.bossInfo.phase;
        this.bossPulseT = Date.now() + 500;
      }

      this.bossBarBg.roundRect(bx, by, bw, bh, br);
      this.bossBarBg.fill({ color: 0x110011, alpha: 0.85 });
      this.bossBarBg.stroke({ width: 2, color: phaseCol, alpha: 0.85 });

      // Phase dividers at 33%/66% — bigger glowing notches that read clearly
      if (this.bossInfo.kind === 'boss') {
        for (const t of [1 / 3, 2 / 3]) {
          // Outer glow line above + below the bar
          this.bossBarBg.rect(bx + bw * t - 1.5, by - 2, 3, 2);
          this.bossBarBg.fill({ color: phaseCol, alpha: 0.85 });
          this.bossBarBg.rect(bx + bw * t - 1.5, by + bh, 3, 2);
          this.bossBarBg.fill({ color: phaseCol, alpha: 0.85 });
          // Notch through the bar body
          this.bossBarBg.rect(bx + bw * t - 1, by + 1, 2, bh - 2);
          this.bossBarBg.fill({ color: 0xffffff, alpha: 0.55 });
        }
      }

      if (this.bossHpDisp > 0.001) {
        this.bossBar.roundRect(bx + 2, by + 2, Math.max(2, (bw - 4) * this.bossHpDisp), bh - 4, br - 1);
        this.bossBar.fill({ color: phaseCol });
        // Bright leading-edge highlight
        const fillW = Math.max(2, (bw - 4) * this.bossHpDisp);
        this.bossBar.rect(bx + 2 + fillW - 3, by + 3, 3, bh - 6);
        this.bossBar.fill({ color: 0xffffff, alpha: 0.7 });
      }

      // Glow filter color tracks phase, and outer-strength pulses up briefly on phase shift
      if (this.bossBarGlow) {
        this.bossBarGlow.color = phaseCol;
        const pulseRemaining = Math.max(0, this.bossPulseT - Date.now()) / 500;
        const baseStrength = this.bossInfo.phase === 2 ? 3.2 : this.bossInfo.phase === 1 ? 2.6 : 2.0;
        this.bossBarGlow.outerStrength = baseStrength + pulseRemaining * 3.5;
      }

      const phaseLbl = this.bossInfo.phase === 0 ? 'PHASE 1' : this.bossInfo.phase === 1 ? 'ENRAGÉ' : 'CRITIQUE';
      this.bossBarText.text = `${this.bossInfo.kind === 'boss' ? 'BOSS' : 'MINI-BOSS'}  —  ${phaseLbl}`;
      this.bossBarText.x = VW / 2;
      this.bossBarText.y = by + bh + 4;
      this.bossBarText.style.fill = phaseCol;
    } else if (this.bossHpDisp !== 1) {
      // Reset when boss not in scene so a new boss starts at full
      this.bossHpDisp = 1;
    }

    if (wave.summaryT > 0 && wave.prevStats) {
      const s = wave.prevStats;
      const acc = s.shotsFired > 0 ? Math.round((s.shotsHit / s.shotsFired) * 100) : 0;
      const lines = [
        `VAGUE ${wave.wave - 1} TERMINÉE`,
        '',
        `éliminations  ${s.kills}`,
        `précision     ${acc}%`,
        `dégâts subis  ${s.dmgTaken}`,
        `meilleur combo ×${s.bestCombo}`,
      ];
      this.summaryText.text = lines.join('\n');
      this.summaryText.x = VW / 2;
      this.summaryText.y = VH / 2;
      this.summaryText.alpha = Math.min(1, wave.summaryT * 4);
      const tw = this.summaryText.width + 60;
      const th = this.summaryText.height + 40;
      this.summaryBg.clear();
      this.summaryBg.roundRect(VW / 2 - tw / 2, VH / 2 - th / 2, tw, th, 10);
      this.summaryBg.fill({ color: 0x000000, alpha: 0.7 * Math.min(1, wave.summaryT * 4) });
      this.summaryBg.stroke({ width: 2, color: 0xffaa00, alpha: 0.8 * Math.min(1, wave.summaryT * 4) });
    } else {
      this.summaryText.text = '';
      this.summaryBg.clear();
    }

    if (wave.banner && wave.banner.t > 0) {
      const a = Math.min(1, wave.banner.t / 30);
      const txt = wave.banner.text + (wave.preview && wave.banner.t > 100 ? '\nÀ VENIR : ' + wave.preview : '');
      this.bannerText.text = txt;
      this.bannerText.style.fill = wave.banner.color;
      // Sync glow to banner color so the halo matches the message
      const bf = this.bannerText.filters as any;
      if (bf && bf[0]) bf[0].color = wave.banner.color;
      this.bannerText.x = VW / 2;
      this.bannerText.y = 140;
      this.bannerText.alpha = a;
      this.bannerBg.clear();
      const tw = Math.max(this.bannerText.width + 60, 380);
      const th = this.bannerText.height + 30;
      const bx = VW / 2 - tw / 2;
      const by = 105;
      // Shadow underlay
      this.bannerBg.rect(bx + 4, by + 4, tw, th);
      this.bannerBg.fill({ color: 0x000000, alpha: 0.4 * a });
      // Main bg
      this.bannerBg.rect(bx, by, tw, th);
      this.bannerBg.fill({ color: 0x110011, alpha: 0.78 * a });
      this.bannerBg.stroke({ width: 2, color: wave.banner.color, alpha: 0.85 * a });
    } else {
      this.bannerText.text = '';
      this.bannerBg.clear();
    }
  }
}
